import { z } from "zod";
import type { CsvImportRun } from "./workflows/csv-import";

export const importKindSchema = z.enum(["signals", "people", "companies"]);
export type ImportKind = z.infer<typeof importKindSchema>;
export const importFields = [
  "source",
  "external_id",
  "message_url",
  "message_text",
  "occurred_at",
  "author_name",
  "author_id",
  "author_url",
  "email",
  "linkedin_url",
  "person_id",
  "company_name",
  "company_domain",
  "company_id",
  "notes",
] as const;
export type ImportField = (typeof importFields)[number];
export const importSettingsSchema = z.object({
  mapping: z.partialRecord(z.enum(importFields), z.string()),
  sourceId: z.uuid().nullable(),
  sources: z.record(z.string(), z.uuid()).default({}),
});
export type ImportSettings = z.infer<typeof importSettingsSchema>;
export const importResolutionSchema = z.object({
  personId: z.uuid().optional(),
  companyId: z.uuid().optional(),
  createPerson: z.boolean().optional(),
  createCompany: z.boolean().optional(),
  skip: z.boolean().optional(),
});
export type ImportResolution = z.infer<typeof importResolutionSchema>;
export const manualSourceSchema = z.object({
  name: z.string().trim().min(1),
  provider: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_-]*$/),
  url: z.url().refine((v) => /^https?:\/\//.test(v), "Use an HTTP or HTTPS URL."),
});
export type ImportSource = {
  id: string;
  name: string;
  provider: string;
  url: string;
  manual: boolean;
};
export type ImportCandidate = { id: string; name: string; detail: string | null };
export type ImportPlan = {
  errors: string[];
  personId: string | null;
  companyId: string | null;
  signalId: string | null;
  personCandidates: ImportCandidate[];
  companyCandidates: ImportCandidate[];
  sourceId: string | null;
  personAction: string;
  companyAction: string;
  signalAction: string;
};
export type ImportRow = {
  number: number;
  data: Record<string, string>;
  resolution: ImportResolution;
  status: string;
  result: Record<string, string>;
  plan?: ImportPlan;
};
export type ImportFile = {
  id: string;
  kind: ImportKind;
  filename: string;
  headers: string[];
  settings: ImportSettings;
  createdAt: string;
  uploader: string;
  total: number;
  completed: number;
};
export type ImportDetail = ImportFile & { rows: ImportRow[]; page: number; sourceValues: string[] };
type Json<T> = { content: { "application/json": T } };
type Id = { path: { id: string } };
export interface ImportPaths {
  "/imports": {
    get: { responses: { 200: Json<ImportFile[]> } };
    post: {
      requestBody: Json<{ kind: ImportKind; filename: string; csv: string }>;
      responses: { 201: Json<ImportFile> };
    };
  };
  "/imports/sources": {
    get: { responses: { 200: Json<ImportSource[]> } };
    post: {
      requestBody: Json<z.infer<typeof manualSourceSchema>>;
      responses: { 201: Json<ImportSource> };
    };
  };
  "/imports/{id}": {
    get: { parameters: Id & { query?: { page?: string } }; responses: { 200: Json<ImportDetail> } };
    patch: {
      parameters: Id;
      requestBody: Json<ImportSettings>;
      responses: { 200: Json<ImportFile> };
    };
  };
  "/imports/{id}/rows/{number}": {
    patch: {
      parameters: { path: { id: string; number: string } };
      requestBody: Json<ImportResolution>;
      responses: { 204: { content?: never } };
    };
  };
  "/imports/{id}/run": { get: { parameters: Id; responses: { 200: Json<CsvImportRun | null> } } };
  "/imports/{id}/commit": {
    post: {
      parameters: Id;
      responses: { 202: Json<{ workflowId: string; runId: string }> };
    };
  };
}
